import React, { useMemo } from 'react';

import { RouterConfig, RouteConfig, PermissionedRoute, RouteAccessProps } from 'types';
import refine, { Refinable } from 'utils/refine';
import { useAuthorization } from 'components/Authorization';
import { useRouteAccessProps } from 'hooks/business/useNewPermisson';

/**
 * 获取权限路由信息
 * @param initialState 初始状态
 * @param routes 路由配置项
 */
export function getPermissionedRoutes(accessProps: RouteAccessProps, routes: RouteConfig[]) {
  return routes.reduce<PermissionedRoute[]>((result, route) => {
    const { access, children } = route;

    const permission = access === undefined ? 'write' : access(accessProps);

    if (permission) {
      result.push({
        ...route,
        permission,
        children: children ? getPermissionedRoutes(accessProps, children) : undefined,
      });
    }

    return result;
  }, []);
}

/**
 * 未登录时使用pulicRoutes解析，绕开所有需要依赖登录态的判断
 * @param routes
 * @returns
 */
export function getPublicRoutes(routes: RouteConfig[]) {
  return routes.reduce<PermissionedRoute[]>((result, route) => {
    const { access, children } = route;
    /**
     * 因为未登录，route拥有自定义access的话，一律认为没有权限
     */
    const permission = access === undefined ? 'write' : false;

    if (permission) {
      result.push({
        ...route,
        permission,
        children: children ? getPublicRoutes(children) : undefined,
      });
    }

    return result;
  }, []);
}

/**
 * 路由元信息 `context` 值
 */
export interface RouterMetaContextValue extends Required<Omit<RouterConfig, 'history'>> {
  /**
   * 权限路由信息
   */
  permissionedRoutes: PermissionedRoute[];
  /**
   * 匹配路由信息
   */
}

/**
 * 路由元信息属性
 */
export interface RouterMetaProps extends Omit<RouterConfig, 'history'> {
  /**
   * 子节点或子节点渲染函数
   */
  children?: Refinable<(meta: RouterMetaContextValue) => React.ReactNode>;
}

/**
 * 路由元信息
 */
function AuthedRouterMeta(props: RouterMetaProps) {
  const { basename = '', routes, children } = props;

  const accessProps = useRouteAccessProps();

  const permissionedRoutes = useMemo(() => {
    return routes ? getPermissionedRoutes(accessProps, routes) : [];
  }, [accessProps, routes]);
  const routerMetaContextValue = useMemo(() => {
    return {
      basename,
      routes: routes || [],
      permissionedRoutes,
    };
  }, [basename, permissionedRoutes, routes]);
  return <>{refine(children, routerMetaContextValue)}</>;
}

function PublicRouterMeta(props: RouterMetaProps) {
  const { basename = '', routes, children } = props;

  const permissionedRoutes = useMemo(() => {
    return routes ? getPublicRoutes(routes) : [];
  }, [routes]);
  const routerMetaContextValue = useMemo(() => {
    return {
      basename,
      routes: routes || [],
      permissionedRoutes,
    };
  }, [basename, permissionedRoutes, routes]);
  return <>{refine(children, routerMetaContextValue)}</>;
}
const RouterMeta = (props: RouterMetaProps) => {
  const auth = useAuthorization();
  return useMemo(() => {
    if (!auth.authorized) {
      // 未登录时使用公开路由的meta信息
      return <PublicRouterMeta {...props} />;
    }
    return <AuthedRouterMeta {...props} />;
  }, [auth.authorized, props]);
};
export default RouterMeta;
